import React, { useEffect } from 'react';
import {BrowserRouter, Route, Routes, useLocation} from 'react-router-dom';
import { useToast } from '../modules/layout/components/ToastContext';
import App from '../../App';
import Home from '../pages/Home';
import CallbackPage from '../modules/auth/components/CallbackPage';
import NotFoundPage from '../pages/NotFoundPage';
import PrivateRoutes from './PrivateRoutes';
import ServiceList from '../pages/Services/ServiceList';
import ErrorsPage from '../modules/errors/ErrorsPage';
import LoginPage from '../modules/auth/LoginPage';
import LogoutPage from '../modules/auth/LogoutPage';
import ServiceDetails from '../pages/Services/ServiceDetails';
import ServiceForm from '../pages/Services/ServiceForm';
import TransactionList from "../pages/Transations/TransactionList";
import TransactionDetails from "../pages/Transations/TransactionDetails";
import TransactionParameterList from "../pages/TransactionParameters/TransactionParameterList";
import TransactionParameterForm from "../pages/TransactionParameters/TransactionParameterForm";
import ServiceDataDifferences from '../pages/DataDifferences/ServiceDataDifferences';
import TransactionDataDifferences from '../pages/DataDifferences/TransactionDataDifferences';
import TransactionParameterDataDifferences from '../pages/DataDifferences/TransactionParameterDataDifferences';
import TransactionParameterDetails from "../pages/TransactionParameters/TransactionParameterDetails";
import TransactionForm from "../pages/Transations/TransactionForm";
import LibraryList from "../pages/Libraries/LibraryList";
import ClaimForm from '../pages/Claims/ClaimForm';
import ClaimList from '../pages/Claims/ClaimList';
import AuditLogList from "../pages/AuditLogs/AuditLogList";
import AuditLogDetails from "../pages/AuditLogs/AuditLogDetails";
import LibraryForm from '../pages/Libraries/LibraryForm';
import AddLibrariesToTransactionRoute from '../pages/Libraries/AddLibrariesToTransactionRoute';
import InstitutionList from '../pages/Institutions/InstitutionList';
import InstitutionDetails from '../pages/Institutions/InstitutionDetails';
import InstitutionForm from '../pages/Institutions/InstitutionForm';
import TemplateClaimList from '../pages/TemplateClaims/TemplateClaimList';
import TemplateClaimForm from '../pages/TemplateClaims/TemplateClaimForm';
import GatewayLogList from '../pages/GatewayLogs/GatewayLogList';
import GatewayLogDetails from '../pages/GatewayLogs/GatewayLogDetails';
import ConsumptionLogList from '../pages/ConsumptionLogs/ConsumptionLogList';
import ConsumptionLogDetails from '../pages/ConsumptionLogs/ConsumptionLogDetails';
import ConsumptionReports from '../pages/ConsumptionLogs/ConsumptionReports';
import TransactionExplorerList from '../pages/TransactionExplorer/TransactionExplorerList';
import TransactionExplorerDetails from '../pages/TransactionExplorer/TransactionExplorerDetails';
import EditLibraryForm from '../pages/Libraries/EditLibraryForm';
import ClientsList from '../pages/Clients/ClientsList';
import ProfileEventsList from '../pages/UserProfileEvents/ProfileEventsList';
import FindProfiles from '../pages/UserProfileEvents/FindProfilesList';
import ProfileDetails from '../pages/UserProfileDetails/ProfileDetails';
import EditProfile from '../pages/UserProfileDetails/UpdateProfile';
import ManageAccounts from '../pages/ManageAccounts/ManageAccounts';
import AssignRole from '../pages/ManageAccounts/UserRole';
import ProfileEventsHistoryMongodb from '../pages/ProfileEventsHistoryMongodb/ProfileEventsHistoryMongodb';
import Statistics from '../pages/Statistics/Statistics';

const ToastNavigationListener = () => {
    const { hideToast } = useToast();
    const location = useLocation();
    useEffect(() => {
        hideToast();
    }, [location.pathname]);
    return null;
};

const AppRoutes = () => (
    <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
        <ToastNavigationListener />
        <Routes>
            <Route element={<App/>}>

                {/* Public routes */}
                <Route path="/" element={<Home/>}/>
                <Route path="/auth/callback" element={<CallbackPage/>}/>

                <Route path='error/*' element={<ErrorsPage/>}/>
                <Route path="*" element={<NotFoundPage/>}/>

                <Route path="/auth/login" element={<LoginPage/>}/>
                <Route path="/logout" element={<LogoutPage/>}/>

                {/* Protected routes */}
                <Route element={<PrivateRoutes/>}>
                    <Route path="/institutions/list" element={<InstitutionList/>}/>
                    <Route path="/institutions/create" element={<InstitutionForm/>}/>
                    <Route path="/institutions/edit/:id" element={<InstitutionForm/>}/>
                    <Route path="/institutions/details/:id" element={<InstitutionDetails/>}/>

                    <Route path="/services/list" element={<ServiceList key="list"/>}/>
                    <Route path="/services/list/:institutionid" element={<ServiceList key="institution_list"/>}/>
                    <Route path="/services/details/:id" element={<ServiceDetails/>}/>
                    <Route path="/services/create" element={<ServiceForm/>}/>
                    <Route path="/services/edit/:id" element={<ServiceForm/>}/>
                    <Route path="/services/datadiff/:id/list" element={<ServiceDataDifferences/>}/>

                    <Route path="/services/:serviceid/transactions/list" element={<TransactionList/>}/>
                    <Route path="/services/:serviceid/transactions/details/:id" element={<TransactionDetails/>}/>
                    <Route path="/services/:serviceid/transactions/create" element={<TransactionForm/>}/>
                    <Route path="/services/:serviceid/transactions/edit/:id" element={<TransactionForm/>}/>
                    <Route path="/services/:serviceid/transactions/datadiff/:id/list"
                           element={<TransactionDataDifferences/>}/>
                    <Route path="/services/:serviceid/transactions/claimbuilder/:id"
                           element={<ClaimForm/>}/>

                    <Route path="/services/:serviceid/transactions/:transactionid/transaction-parameters/details/:id"
                           element={<TransactionParameterDetails/>}/>
                    <Route path="/services/:serviceid/transactions/:transactionid/transaction-parameters/list"
                           element={<TransactionParameterList/>}/>
                    <Route path="/services/:serviceid/transactions/:transactionid/transaction-parameters/create"
                           element={<TransactionParameterForm/>}/>
                    <Route path="/services/:serviceid/transactions/:transactionid/transaction-parameters/edit/:id"
                           element={<TransactionParameterForm/>}/>
                    <Route path="/services/:serviceid/transactions/:transactionid/transaction-parameters/datadiff/:id/list"
                        element={<TransactionParameterDataDifferences />} />

                    <Route path="/services/:serviceid/transactions/:transactionid/libraries/list" element={<LibraryList />} />
                    <Route
                        path="/services/:serviceid/transactions/:transactionid/libraries/add"
                        element={<AddLibrariesToTransactionRoute />}
                    />
                    <Route path="/services/:serviceid/transactions/:transactionid/libraries/upload" element={<LibraryForm />} />
                    <Route
                        path="/services/:serviceid/transactions/:transactionid/libraries/edit/:libraryId"
                        element={<EditLibraryForm />}
                    />

                    <Route path="/claims/list" element={<ClaimList/>}/>

                    <Route path="/audit-logs/list" element={<AuditLogList/>}/>
                    <Route path="/audit-logs/details/:id" element={<AuditLogDetails/>}/>

                    <Route path="/gateway-logs/list" element={<GatewayLogList/>}/>
                    <Route path="/gateway-logs/details/:id" element={<GatewayLogDetails/>}/>

                    <Route path="/transaction-explorer" element={<TransactionExplorerList/>}/>
                    <Route path="/transaction-explorer/:className" element={<TransactionExplorerDetails/>}/>

                    <Route path="/consumption-logs/list" element={<ConsumptionLogList/>}/>
                    <Route path="/consumption-logs/details/:id" element={<ConsumptionLogDetails/>}/>
                    <Route path="/consumption-logs/reports" element={<ConsumptionReports/>}/>

                    <Route path="/templateclaims/list" element={<TemplateClaimList/>}/>
                    <Route path="/templateclaims/create" element={<TemplateClaimForm/>}/>
                    <Route path="/templateclaims/edit/:id" element={<TemplateClaimForm/>}/>

                    <Route path="/clients/list" element={<ClientsList/>} />
                    <Route path="/profiles/events" element={<ProfileEventsList/>} />
                    <Route path="/profiles/events/:profileId" element={<ProfileEventsList/>} />
                    <Route path="/find-profiles" element={<FindProfiles />} />
                    <Route path="/profile-details/:targetProfileId" element={<ProfileDetails />} />
                    <Route path="/profile-details/:targetProfileId/edit" element={<EditProfile/>} />
                    <Route path="/manage-accounts" element={<ManageAccounts/>}/>
                    <Route path="/manage-accounts/assign-profile-role/:targetProfileId" element={<AssignRole/>}/>
                    <Route path="/profile-events-history" element={<ProfileEventsHistoryMongodb />} />
                    <Route path="/statistics" element={<Statistics />} />

                </Route>

            </Route>
        </Routes>
    </BrowserRouter>
);

export default AppRoutes;
